import { RankPatentBadge } from '@/components/ranks/RankPatentBadge';
import { orderRankFamilies } from '@/lib/ranks/orderRankFamilies';
import {
  familyExtraBadges,
  seasonFamilyRestTierId,
} from '@/lib/ranks/seasonFamilyDisplay';
import type { ClipperRankFamily } from '@/lib/ranks/types';

type RankFamilyNestsProps = {
  families: ClipperRankFamily[];
  leadingFamilyId?: string | null;
  labeledFamilyId?: string | null;
  seasonTitleId?: string | null;
  displayedSeasonId?: string | null;
};

function restTierIdFor(
  family: ClipperRankFamily,
  seasonTitleId?: string | null,
): string | null {
  if (family.id === 'season') {
    return seasonFamilyRestTierId({
      titleId: seasonTitleId,
      featuredTierId: family.featured_tier_id,
    });
  }
  return family.featured_tier_id || family.badges[0]?.tier_id || null;
}

export function RankFamilyNests({
  families,
  leadingFamilyId,
  labeledFamilyId,
  seasonTitleId,
  displayedSeasonId,
}: RankFamilyNestsProps) {
  const ordered = orderRankFamilies({ families, leadingFamilyId });

  return (
    <div
      data-testid="clipper-rank-family-nests"
      className="flex flex-wrap items-center gap-1"
    >
      {ordered.map((family) => {
        const restTierId = restTierIdFor(family, seasonTitleId);
        if (!restTierId) return null;

        const restBadge =
          (displayedSeasonId
            ? family.badges.find(
                (badge) =>
                  badge.tier_id === restTierId &&
                  badge.season_id === displayedSeasonId,
              )
            : undefined) ??
          family.badges.find((badge) => badge.tier_id === restTierId);
        const extraBadges = familyExtraBadges({
          family,
          restTierId,
          displayedSeasonId,
        });

        return (
          <span
            key={family.id}
            data-testid={`clipper-rank-family-${family.id}`}
            className="inline-flex items-center gap-1"
          >
            <RankPatentBadge
              tierId={restTierId}
              size="sm"
              showLabel={family.id === labeledFamilyId}
              seasonName={restBadge?.season_name}
            />
            {extraBadges.length > 0 ? (
              <span
                data-testid="clipper-rank-family-extras"
                className="inline-flex items-center"
              >
                {extraBadges.map((badge, index) => (
                  <RankPatentBadge
                    key={`${badge.tier_id}:${badge.season_id ?? index}`}
                    tierId={badge.tier_id}
                    size="sm"
                    showLabel={false}
                    seasonName={badge.season_name}
                    className={index === 0 ? undefined : '-ml-2'}
                  />
                ))}
              </span>
            ) : null}
          </span>
        );
      })}
    </div>
  );
}
